import React, { FC, useContext, useState, useEffect } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import XCircle from '@components/Icons/XCircle';
import Buttons from '@components/Buttons';
import TeamsContext from '@providers/Teams/TeamsContext';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import ChatDropDown from '@components/Chat/ChatDropDown';
import * as ApiModels from '@typings/api-models';
import OutsideClick from '@components/OutsideClick';
import Signature from '@components/Icons/Signature';
import s from './InputComposer.module.scss';
import SearchInput from '../FormFields/SearchInput';

type Props = {
  onAttach: (data: { customTermsTemplateId: string; title: string; }) => void;
};

const AddCustomTerms: FC<Props> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const {
    data: customTerms,
    getData: getCustomTerms,
    fetchingState: { isFetching: isFetchingCustomTerms },
  } = useNoodleApi(tsClient.customTerms.getCustomTermsTemplates);

  useEffect(() => {
    if (creatorId) {
      getCustomTerms({ creatorId, page: 1, perPage: 1000 });
    }
  }, [creatorId, getCustomTerms]);

  const toggle = (): void => {
    setPopoverOpen(prev => !prev);
  };

  const handleClick = (template: Pick<ApiModels.CustomTermsTemplate, 'id' | 'title'>): void => {
    setPopoverOpen(false);
    onAttach({ customTermsTemplateId: template.id, title: template.title || template.id });
  };

  if (customTerms?.items.length === 0) {
    return null;
  }

  return (
    <>
      <Buttons
        ariaLabel={'Insert Custom Terms'}
        iconBefore={
          <m.div
            style={{
              alignItems: 'center',
              display: 'flex',
              height: '16px',
              justifyContent: 'center',
              width: '16px',
              willChange: 'transform',
            }}
            whileTap={{ scale: 0.9 }}
          >
            {popoverOpen
              ? (
                <XCircle weight="fill" color="var(--color-gray-50)" size={24} />
              )
              : (
                <Signature size={16} weight="fill" color="var(--color-primary)" />
              )}
          </m.div>
        }
        onClick={toggle}
      >
        Custom Terms
      </Buttons>
      <AnimatePresence>
        {popoverOpen && (
          <OutsideClick onOutsideClick={() => setPopoverOpen(false)} className={s.outsideClick}>
            <ChatDropDown>
              {!customTerms || isFetchingCustomTerms
                ? (
                  <ProgressIndicator isCentered />
                )
                : (
                  <>
                    <SearchInput label="Filter by title" placeholder="Type a title..." setSearch={setSearch} className={s.search} />
                    {customTerms.items
                      .filter(template => template.title?.toLowerCase().includes(search.toLowerCase()))
                      .map(template => (
                        <Buttons className={s.documentRequestButton} key={template.id} isWrapper isFlat onClick={() => handleClick(template)}>
                          <Signature size={14} weight="fill" color="var(--color-primary)" />
                          <p>
                            {template.title}
                          </p>
                        </Buttons>
                      ))}
                  </>
                )}
            </ChatDropDown>
          </OutsideClick>
        )}
      </AnimatePresence>
    </>
  );
};

export default AddCustomTerms;
